import { useEffect, useMemo, useState } from 'react'
import { removeItem } from 'actions/CheckoutActions'
import { removeFromUserCart } from 'actions/UserCartActions'
import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import {
  getExperienceReservationMetadata,
  hasExperienceReservationMetadata,
  isExperienceReservationExpired,
} from 'lib/experiences/experienceReservationUtils'
import { getCartMachineActor } from 'machines/cart/getCartMachineActor'
import { selectItemsInEffectiveVisibleCart } from 'machines/cart/selectors'

function selectExperienceCartItems(state: App.State, experienceId: string) {
  return state.checkout.cart.items.filter(
    (item): item is App.Checkout.ExperienceItem =>
      item.itemType === 'experience' && item.experienceId === experienceId,
  )
}

function getMultiCartExperienceItems(experienceId: string) {
  const snapshot = getCartMachineActor()?.getSnapshot()
  if (!snapshot) return []

  return selectItemsInEffectiveVisibleCart(snapshot)
    .map(entry => entry.item)
    .filter((item): item is App.Checkout.ExperienceItem =>
      item.itemType === 'experience' && item.experienceId === experienceId,
    )
}

function getExpiredReservationItemIds(items: Array<App.Checkout.ExperienceItem>, now: number) {
  return items
    .filter(item => isExperienceReservationExpired(getExperienceReservationMetadata(item), now))
    .map(item => item.itemId)
}

export function useRemoveExpiredExperienceReservationsFromCart(experienceId: string) {
  const dispatch = useAppDispatch()
  const cartItems = useAppSelector(
    state => selectExperienceCartItems(state, experienceId),
    shallowEqual,
  )
  const [now, setNow] = useState(Date.now())

  const hasTrackedReservations = useMemo(() => {
    const multiCartItems = getMultiCartExperienceItems(experienceId)
    return cartItems.some(hasExperienceReservationMetadata)
      || multiCartItems.some(hasExperienceReservationMetadata)
  }, [cartItems, experienceId])

  const expiredCheckoutItemIds = useMemo(
    () => getExpiredReservationItemIds(cartItems, now),
    [cartItems, now],
  )

  useEffect(() => {
    if (!hasTrackedReservations) return

    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [hasTrackedReservations])

  useEffect(() => {
    const multiCartItems = getMultiCartExperienceItems(experienceId)
    const expiredMultiCartItemIds = getExpiredReservationItemIds(multiCartItems, now)
      .filter(itemId => !cartItems.some(item => item.itemId === itemId))

    expiredCheckoutItemIds.forEach(itemId => {
      dispatch(removeItem(itemId))
    })

    if (expiredMultiCartItemIds.length > 0) {
      dispatch(removeFromUserCart(expiredMultiCartItemIds))
    }
  }, [dispatch, expiredCheckoutItemIds, experienceId, cartItems, now])
}
